import {} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import React, { FC, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import { StyledCard } from "./Invite.styled";
import { useBuyersHubStore } from "../BuyersHub.store";

import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@components/base";
import {
  useGetCurrentCompanyInfo,
  useGetProcurementSuppliers,
  useSendInviteToProcurementSuppliers,
} from "@api";
import { ConfirmationModal } from "@components/common";
import { useUserStore } from "@store/user";

const Invite: FC = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { enqueueSnackbar } = useSnackbar();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  const { currentCompanyInfo, currentCompanyInfoEnabled } =
    useGetCurrentCompanyInfo(user);
  const { procurementSuppliers } = useGetProcurementSuppliers(user);
  const { inviteSuppliersAsync, inviteSuppliersEnabled, isPendingInviteSuppliers } =
    useSendInviteToProcurementSuppliers(user);
  const { supplierIds, setSupplierIds } = useBuyersHubStore();

  const hasNotInvitedSuppliers = useMemo(() => {
    return (
      procurementSuppliers?.items?.some(
        (supplier) => !supplier.isRegistrationLinkSent,
      ) || !isEmpty(supplierIds)
    );
  }, [procurementSuppliers, supplierIds]);

  //TODO: add translations for snackbars, also check for other places where this is used

  const handleSendInvites = async (): Promise<void> => {
    if (procurementSuppliers?.items) {
      try {
        const invitePayload = isEmpty(supplierIds)
          ? {}
          : { supplierIds: supplierIds };

        await inviteSuppliersAsync(invitePayload);
        enqueueSnackbar(t("snackbar.message.invitesSentSuccessfully"), {
          variant: "success",
          autoHideDuration: 3000,
        });
        setIsConfirmModalOpen(false);
        setSupplierIds([]);
      } catch {
        enqueueSnackbar(t("snackbar.message.invitesNotSent"), {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    }
  };

  const getLink = (): void => {
    const url = `${window.location.origin}/register-supplier/${currentCompanyInfo?.id}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        enqueueSnackbar(t("snackbar.message.successfullyCoppiedInviteLink"), {
          variant: "success",
          autoHideDuration: 3000,
        });
      })
      .catch(() => {
        enqueueSnackbar(t("snackbar.error.notCoppiedInviteLink"), {
          variant: "error",
          autoHideDuration: 3000,
        });
      });
  };

  return (
    currentCompanyInfoEnabled && (
      <>
        <Card>
          <Stack p={3} spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5">
                {t("invite.registrationLinkTitle")}
              </Typography>
              <IconButton size="small" color="primary">
                <FeatherIcon icon="info" size={16} />
              </IconButton>
            </Stack>
            {inviteSuppliersEnabled && (
              <>
                <StyledCard variant="outlined">
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    p={2}
                    spacing={2}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Chip
                        label={
                          hasNotInvitedSuppliers
                            ? t("invite.available")
                            : t("invite.unavailable")
                        }
                        color={hasNotInvitedSuppliers ? "blue2" : "default"}
                        variant="filled"
                        size="medium"
                      />
                      <Divider orientation="vertical" flexItem />
                      <Typography variant="body2">
                        <strong>{t("invite.sendInvitesDescription")}</strong>&nbsp;
                        {t("invite.sendInvitesInfo", {
                          companyName: currentCompanyInfo?.name,
                        })}
                      </Typography>
                    </Stack>
                    <Box>
                      <Button
                        onClick={() => setIsConfirmModalOpen(true)}
                        variant="contained"
                        disabled={
                          !hasNotInvitedSuppliers || isPendingInviteSuppliers
                        }
                        startIcon={<FeatherIcon icon="mail" size={16} />}
                        size="extraSmall"
                      >
                        {isEmpty(supplierIds)
                          ? t("invite.sendInvitesButton")
                          : `${t("invite.sendSelectedInvitesButton")} (${supplierIds.length})`}
                      </Button>
                    </Box>
                  </Stack>
                </StyledCard>
                <Typography align="center">{t("invite.or")}</Typography>
              </>
            )}
            <StyledCard variant="outlined">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                p={2}
                spacing={2}
              >
                <Typography variant="body2">
                  <strong>{t("invite.copyLinkDescription")}</strong>&nbsp;
                  {t("invite.copyLinkInfo", {
                    companyName: currentCompanyInfo?.name,
                  })}
                </Typography>
                <Box>
                  <Button
                    onClick={() => getLink()}
                    variant="contained"
                    startIcon={<FeatherIcon icon="copy" size={16} />}
                    size="extraSmall"
                  >
                    {t("invite.copyLink")}
                  </Button>
                </Box>
              </Stack>
            </StyledCard>
          </Stack>
        </Card>
        <ConfirmationModal
          open={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleSendInvites}
          title={t("invite.modalTitle")}
          description={t("invite.modalDescription", {
            companyName: currentCompanyInfo?.name,
          })}
          confirmText={t("invite.modalConfirm")}
        />
      </>
    )
  );
};

export default Invite;
